// src/views/CollectorDash.js — extracted from App.js
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { compressImage, detectWatchFromPhoto, fileToBase64, cropPhotoFromId, validateGovId, extractIdDetails } from "../lib/utils";
import { SK } from "../constants";

// ─── Request Collector Access ─────────────────────────────────────────────────
export function RequestCollectorView({ collectorRequests, saveCRs, session, openAuth, pendingCR, toast }) {
  const [reason,setReason] = useState("");
  const [done,setDone]     = useState(false);
  const [idFile,setIdFile] = useState(null);
  const [idUpl,setIdUpl]   = useState(false);
  const idRef = useRef();

  if (!session) return (
    <div style={{textAlign:"center",padding:"80px 0"}} className="fade-in">
      <h2 style={{fontSize:36,marginBottom:12}}>Collector Access</h2>
      <p style={{color:"#4A423A",fontSize:14,marginBottom:24}}>Sign in first, then request collector access to add watches to the catalog.</p>
      <button className="btn-ink" onClick={()=>openAuth("buyer")}>Sign in →</button>
    </div>
  );

  if (pendingCR || done) return (
    <div style={{textAlign:"center",padding:"80px 0"}} className="fade-in">
      <div style={{fontSize:48,color:"#B8860B",marginBottom:12}}>◷</div>
      <h2 style={{fontSize:36,marginBottom:8}}>Request pending.</h2>
      <p style={{color:"#4A423A",fontSize:14}}>Admin will review your application. You'll be able to add watches once approved.</p>
    </div>
  );

  const already = collectorRequests.find(r=>r.userId===session.user.id && r.status==="approved");
  if (already) return (
    <div style={{textAlign:"center",padding:"80px 0"}} className="fade-in">
      <div style={{fontSize:48,color:"#2E7D52",marginBottom:12}}>✓</div>
      <h2 style={{fontSize:36,marginBottom:8}}>You're a Collector.</h2>
      <p style={{color:"#4A423A",fontSize:14}}>Your role should have updated. Please sign out and back in if the Add to Catalog button isn't showing.</p>
    </div>
  );

  return (
    <div className="fade-in" style={{maxWidth:580}}>
      <div style={{marginBottom:32,borderRadius:14,overflow:"hidden",border:"1px solid #2E2720"}}>
        <div style={{background:"#1A1612",padding:"28px 28px 24px",color:"#F0E8D8"}}>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.22em",color:"#B8860B",marginBottom:14,textTransform:"uppercase"}}>The Horological Catalog · Collector</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:30,marginBottom:8,lineHeight:1.2}}>Add your collection to the record.</div>
          <p style={{fontSize:13,color:"#A89888",lineHeight:1.75,margin:0}}>Collectors document India's vintage watch heritage — one dial at a time. Your submissions become part of the canonical THC catalog, reviewed and published by our team.</p>
        </div>
        <div style={{background:"#201C18",padding:"20px 28px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:2}}>
          {[
            ["◈","Add watches to the catalog","Photos, specs, decade, condition — full entries."],
            ["✦","Collector badge on your profile","Shown across your catalog submissions."],
            ["◎","Demand signals under your entries","See buyers waiting for what you document."],
            ["⊡","Photo attribution","Your name credited on every entry you submit."],
            ["◉","Help sellers find buyers","Your catalog entries auto-surface to sourcing buyers."],
            ["⬡","Admin-curated quality","Every submission reviewed before it goes live."],
          ].map(([icon,title,desc])=>(
            <div key={title} style={{display:"flex",gap:10,padding:"14px 12px",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:18,color:"#B8860B",flexShrink:0,lineHeight:1,marginTop:2}}>{icon}</div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:"#D4C9BB",marginBottom:3}}>{title}</div>
                <div style={{fontSize:11,color:"#6A6059",lineHeight:1.55}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{background:"#1A1612",padding:"14px 28px",borderTop:"1px solid #2E2720",display:"flex",gap:24,flexWrap:"wrap"}}>
          {[["01","Apply in 2 min"],["02","Admin reviews"],["03","Role upgraded"],["04","Start adding entries"]].map(([n,t])=>(
            <div key={n} style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"#B8860B",letterSpacing:"0.1em"}}>{n}</span>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#6A6059"}}>{t}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{marginBottom:16}}>
        <label className="fl">Why do you want to add to the catalog?</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={4} placeholder="Tell us about your collection, expertise, what you'd like to submit…" autoCapitalize="sentences" required/>
      </div>
      <div style={{marginBottom:16}}>
        <label className="fl">Government ID <span style={{color:"var(--ink3)",fontWeight:400}}>(Aadhaar, PAN, Passport, Driving Licence)</span></label>
        <input ref={idRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>setIdFile(e.target.files[0]||null)}/>
        {idFile ? (
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"var(--gbg)",border:"1px solid var(--gbd)",borderRadius:8}}>
            <img src={URL.createObjectURL(idFile)} alt="ID preview" style={{height:36,borderRadius:4,objectFit:"cover"}}/>
            <span style={{fontSize:12,flex:1,color:"var(--ink2)"}}>{idFile.name}</span>
            <button onClick={()=>setIdFile(null)} style={{background:"none",color:"var(--ink3)",fontSize:16,lineHeight:1}}>×</button>
          </div>
        ) : (
          <button onClick={()=>idRef.current.click()} style={{width:"100%",padding:"10px 0",border:"1.5px dashed var(--bd)",borderRadius:8,background:"transparent",color:"var(--ink3)",fontSize:13,cursor:"pointer"}}>+ Upload ID photo</button>
        )}
      </div>
      <button className="btn-ink" style={{width:"100%",padding:13,fontSize:14}} disabled={idUpl} onClick={async()=>{
        if(!reason.trim()){toast("Please tell us a bit about yourself.");return;}
        if(!idFile){toast("Please upload a government ID.");return;}
        setIdUpl(true);
        const crid="cr"+Date.now();
        let idDocUrl="", avatarUrl="";
        try {
          const compressed=await compressImage(idFile,1600,0.88);
          const path=`collector-ids/${crid}/id.jpg`;
          const {error}=await supabase.storage.from("watch-photos").upload(path,compressed,{contentType:"image/jpeg",upsert:true});
          if (!error) { const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path); idDocUrl=publicUrl; }
        } catch {}
        try {
          const faceBlob=await cropPhotoFromId(idFile);
          if (faceBlob) {
            const {error:fe}=await supabase.storage.from("watch-photos").upload(`collector-ids/${crid}/avatar.jpg`,faceBlob,{contentType:"image/jpeg",upsert:true});
            if (!fe) { const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(`collector-ids/${crid}/avatar.jpg`); avatarUrl=publicUrl; }
          }
        } catch {}
        setIdUpl(false);
        saveCRs([...collectorRequests,{id:crid,userId:session.user.id,name:session.user.name,email:session.user.email,reason,idDocUrl,avatarUrl,status:"pending",createdAt:new Date().toISOString()}]);
        setDone(true);
        toast("Request submitted.");
      }}>{idUpl?"Uploading…":"Submit request →"}</button>
    </div>
  );
}

// ─── Watch bucket grouping ────────────────────────────────────────────────────
// Groups catalog entries so photos + demand signals pool under one watch card.
//
// TODO (AI): replace the normalised-string key with embedding/cluster similarity
// so fuzzy variants ("HMT Pilot" / "HMT Pilot 17J" / "HMT Pilot Black Dial")
// also collapse. Suggested approach: send entry titles to claude-haiku-4-5 in a
// single batch, ask it to return a JSON cluster-id per entry, then use those ids
// as bucket keys instead of the normalised brand|||model string below.

// ─── COLLECTOR DASHBOARD ──────────────────────────────────────────────────────
export function CollectorDash({ catalogEntries, saveCatalog, requests, session, toast }) {
  const [tab, setTab] = useState("entries");
  const [borrowNote, setBorrowNote] = useState({});
  const myEntries  = (catalogEntries||[]).filter(e=>e.collectorId===session?.user?.id);
  const myEntryIds = new Set(myEntries.map(e=>e.id));
  const buyRequests= (requests||[]).filter(r=>r.type==="catalog" && myEntryIds.has(r.catalogEntryId));
  // Watches other collectors are willing to lend
  const lendingPool = (catalogEntries||[]).filter(e=>e.status==="approved"&&e.availableToShare&&e.collectorId!==session?.user?.id);

  const sc={Received:"#B8860B",Searching:"#3730A3","Match Found":"#2E7D52",Dispatched:"#2E7D52",Completed:"#2E7D52"};

  return (
    <div className="fade-in">
      <div style={{marginBottom:28}}>
        <span className="sl">Collector Portal</span>
        <h2 style={{fontSize:36,letterSpacing:"-0.025em"}}>My catalog.</h2>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:28}}>
        {[["Submitted",myEntries.length],["Approved",myEntries.filter(e=>e.status==="approved").length],["Buy Requests",buyRequests.length]].map(([k,v])=>(
          <div key={k} className="card" style={{textAlign:"center",padding:"18px 12px"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,color:"#B8860B",marginBottom:4}}>{v}</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#8A7E74",textTransform:"uppercase"}}>{k}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",borderBottom:"1.5px solid var(--bd)",marginBottom:22}}>
        {[["entries","My Entries"],["requests","Buy Requests"+(buyRequests.length>0?" ("+buyRequests.length+")":"")],["lending","Lending Library"],["add","+ Add New"]].map(([t,l])=>(
          <button key={t} className={`stab ${tab===t?"active":""}`} onClick={()=>switchTab(t)} style={{display:"inline-flex",alignItems:"center",gap:5}}>
            {l.toUpperCase()}
            {t==="lending" && <span style={{fontSize:7,fontFamily:"'DM Mono',monospace",letterSpacing:"0.06em",background:"#FEF3C7",color:"#B45309",border:"1px solid #FCD34D",borderRadius:10,padding:"1px 5px",lineHeight:1.4,fontWeight:700}}>BETA</span>}
          </button>
        ))}
      </div>

      {tab==="entries" && (
        <div>
          {myEntries.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No entries yet. Add your first watch.</div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
            {[...myEntries].reverse().map(e=>(
              <div key={e.id} className="card">
                {e.photos?.length>0 && (
                  <div style={{height:140,borderRadius:8,overflow:"hidden",marginBottom:12,background:"#F4F0EB",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <img src={(e.photos.find(p=>p.isPrimary)?.url||e.photos[0]?.url)+"?width=400&quality=75"} alt={e.model} style={{width:"100%",height:"100%",objectFit:"contain",objectPosition:"center"}}/>
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",textTransform:"uppercase",marginBottom:3}}>{e.brand}{e.year?" · "+e.year:e.decade?" · "+e.decade:""}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{e.model}</div>
                  </div>
                  <span className={`tag ${e.status==="approved"?"tag-grn":e.status==="pending"?"tag-gold":"tag-red"}`}>
                    {e.status==="approved"?"✓ Live":e.status==="pending"?"Pending":"Rejected"}
                  </span>
                </div>
                {e.price && <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#B8860B",marginTop:6}}>₹{e.price.toLocaleString("en-IN")}</div>}
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:8}}>{e.createdAt?.slice(0,10)}</div>
                {e.status==="approved" && (
                  <button onClick={()=>saveCatalog(catalogEntries.map(ce=>ce.id!==e.id?ce:{...ce,availableToShare:!e.availableToShare}))}
                    style={{marginTop:10,width:"100%",fontSize:10,fontFamily:"'DM Mono',monospace",padding:"6px 0",borderRadius:20,border:"1px solid",borderColor:e.availableToShare?"var(--gnbd)":"var(--bd)",background:e.availableToShare?"var(--gnbg)":"transparent",color:e.availableToShare?"var(--grn)":"var(--ink3)",cursor:"pointer",letterSpacing:"0.04em"}}>
                    {e.availableToShare?"✓ Listed for lending":"Offer to lend (1 week)"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab==="lending" && (
        <div>
          <div style={{background:"#FFFBEB",border:"1px solid #FCD34D",borderRadius:12,padding:"14px 18px",marginBottom:20,fontSize:13,color:"#92400E",lineHeight:1.7,display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,flexShrink:0}}>🧪</span>
            <div><strong>Lending Library</strong> <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,background:"#FEF3C7",color:"#B45309",border:"1px solid #FCD34D",borderRadius:10,padding:"1px 6px",letterSpacing:"0.06em",fontWeight:700,verticalAlign:"middle"}}>BETA</span> — watches fellow collectors are offering to share for a week. Trusted collector group only. Request to borrow and the lender will be in touch via the Lounge.</div>
          </div>
          {lendingPool.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No watches available to borrow right now.<br/>Offer one of yours to get the ball rolling.</div>}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {lendingPool.map(e=>{
              const photoSrc = Array.isArray(e.photos)&&e.photos.length>0?(typeof e.photos[0]==="string"?e.photos[0]:e.photos[0]?.url):null;
              return (
                <div key={e.id} className="card" style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                  {photoSrc && <img src={photoSrc} alt="" style={{width:90,height:90,objectFit:"cover",borderRadius:10,border:"1.5px solid var(--bd)",flexShrink:0}}/>}
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:3}}>{e.brand} · {e.decade||""} · by {e.collectorName}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,marginBottom:4}}>{e.brand} {e.model}</div>
                    {e.description && <p style={{fontSize:12.5,color:"#4A423A",lineHeight:1.6,margin:"0 0 10px"}}>{e.description}</p>}
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <span className="tag tag-grn" style={{fontSize:9}}>Available to borrow</span>
                      <textarea value={borrowNote[e.id]||""} onChange={ev=>setBorrowNote(p=>({...p,[e.id]:ev.target.value}))} placeholder="Short note to the lender (optional)…" rows={1} style={{flex:1,minWidth:140,fontSize:11,padding:"6px 10px",borderRadius:8,border:"1px solid var(--bd)",resize:"none"}}/>
                      <button className="btn-green" style={{padding:"7px 14px",fontSize:11,whiteSpace:"nowrap"}} onClick={async()=>{
                        const rid="share-"+Date.now();
                        const entry={id:rid,type:"share_borrow",watch:`${e.brand} ${e.model}`,name:session.user.name,email:session.user.email,userId:session.user.id,lenderId:e.collectorId,lenderName:e.collectorName,catalogEntryId:e.id,message:borrowNote[e.id]||"",status:"Received",amount:0,createdAt:new Date().toISOString(),updates:[]};
                        const {error:brErr}=await supabase.from("requests").insert(entry);
                        if(brErr){toast("Request failed — please retry.");return;}
                        toast("Borrow request sent to "+e.collectorName+"!");
                        setBorrowNote(p=>({...p,[e.id]:""}));
                      }}>Request to borrow →</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab==="requests" && (
        <div>
          {buyRequests.length===0 && <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"#8A7E74"}}>No buy requests yet.</div>}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {[...buyRequests].reverse().map(r=>{
              const entry = myEntries.find(e=>e.id===r.catalogEntryId);
              return (
                <div key={r.id} className="card">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",letterSpacing:"0.1em",marginBottom:4}}>{r.id} · {new Date(r.createdAt).toLocaleDateString("en-IN")}</div>
                      <div style={{fontFamily:"'Playfair Display',serif",fontSize:18}}>{r.watch}</div>
                      {entry && <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:2}}>from your catalog entry</div>}
                    </div>
                    <span className="tag" style={{background:(sc[r.status]||"#8A7E74")+"18",borderColor:(sc[r.status]||"#8A7E74")+"55",color:sc[r.status]||"#8A7E74"}}>{r.status}</span>
                  </div>
                  {r.message && (
                    <div style={{background:"var(--bg2)",borderRadius:8,padding:"10px 14px",marginBottom:10}}>
                      <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginBottom:4}}>BUYER NOTE</div>
                      <p style={{fontSize:13,color:"#4A423A",lineHeight:1.6}}>{r.message}</p>
                    </div>
                  )}
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#4A423A"}}>
                    {r.name} · <a href={"mailto:"+r.email} style={{color:"#B8860B"}}>{r.email}</a>
                  </div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"#8A7E74",marginTop:6}}>₹{r.amount} paid · UTR: {r.txnId||"—"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab==="add" && (
        <AddToCatalogView catalogEntries={catalogEntries} saveCatalog={saveCatalog} session={session} toast={toast}/>
      )}
    </div>
  );
}

// ─── Add to Catalog ───────────────────────────────────────────────────────────

// ─── ADD TO CATALOG VIEW ──────────────────────────────────────────────────────
export function AddToCatalogView({ catalogEntries, saveCatalog, session, toast }) {
  const [photos,setPhotos]     = useState([]);
  const [detecting,setDet]     = useState(false);
  const [form,setForm]         = useState({brand:"",model:"",reference:"",decade:"",year:"",movement:"",company:"",condition:"",description:"",price:""});
  const [submitting,setSub]    = useState(false);
  const [done,setDone]         = useState(false);
  const fileRef                = useRef(null);
  const sf=(k,v)=>setForm(p=>({...p,[k]:v}));

  const addFiles = async (files) => {
    const newPhotos = Array.from(files).map((f,i)=>({file:f,preview:URL.createObjectURL(f),isPrimary:photos.length===0&&i===0}));
    if (photos.length===0 && newPhotos.length>0) {
      setDet(true);
      setPhotos(newPhotos);
      const detected = await detectWatchFromPhoto(newPhotos[0].file);
      if (!detected || detected.brand==="Unknown" && !detected.model) {
        setPhotos([]);
        setDet(false);
        toast("No watch detected in that photo. Please upload a clearer image.");
        return;
      }
      setForm({brand:detected.brand||"",model:detected.model||"",reference:detected.reference||"",decade:detected.decade||"",year:detected.year||"",movement:detected.movement||"",company:detected.company||"",condition:detected.condition||"",description:detected.description||"",price:""});
      toast("AI detected watch details — please review.");
      setDet(false);
    } else {
      setPhotos(p=>[...p,...newPhotos]);
    }
  };

  const removePhoto = (i) => setPhotos(prev=>{
    const next=prev.filter((_,j)=>j!==i);
    if (prev[i].isPrimary && next.length>0) next[0]={...next[0],isPrimary:true};
    return next;
  });

  const submit = async () => {
    if (!form.brand||!form.model||!form.description){toast("Brand, model and description required.");return;}
    if (photos.length===0){toast("At least one photo required.");return;}
    setSub(true);
    const entryId="ce"+Date.now();
    const uploadedPhotos=[];
    for (let i=0;i<photos.length;i++) {
      try {
        const compressed = await compressImage(photos[i].file);
        const path=`${entryId}/${i}.jpg`;
        const {error}=await supabase.storage.from("watch-photos").upload(path,compressed,{upsert:true,contentType:"image/jpeg"});
        if (!error) {
          const {data:{publicUrl}}=supabase.storage.from("watch-photos").getPublicUrl(path);
          uploadedPhotos.push({url:publicUrl,isPrimary:photos[i].isPrimary,order:i});
        }
      } catch {}
    }
    saveCatalog([...catalogEntries,{id:entryId,...form,price:form.price?parseInt(form.price):null,photos:uploadedPhotos,status:"pending",collectorId:session.user.id,collectorName:session.user.name,createdAt:new Date().toISOString()}]);
    setDone(true); setSub(false);
  };

  if (done) return (
    <div style={{textAlign:"center",padding:"80px 0"}} className="fade-in">
      <div style={{fontSize:52,color:"var(--grn)",marginBottom:16}}>✓</div>
      <h2 style={{fontSize:40,marginBottom:8}}>Submitted for review.</h2>
      <p style={{color:"#4A423A",fontSize:14,lineHeight:1.75}}>Admin will review your entry. Once approved it goes live in the catalog.</p>
    </div>
  );

  return (
    <div className="fade-in" style={{maxWidth:640,position:"relative"}}>
      {detecting && (
        <div style={{position:"absolute",inset:0,background:"rgba(240,232,216,.8)",zIndex:10,borderRadius:12,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,opacity:.3,animation:"spin 2s linear infinite"}}>◷</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,letterSpacing:"0.12em",color:"#8A7E74"}}>AI DETECTING WATCH…</div>
        </div>
      )}
      <div style={{marginBottom:32}}>
        <span className="sl">Collector Portal</span>
        <h2 style={{fontSize:38,letterSpacing:"-0.025em",marginBottom:8}}>Add a watch.</h2>
        <p style={{fontSize:14,color:"#4A423A",lineHeight:1.75}}>Upload photos — AI will try to fill in the details. Review everything, then submit for approval.</p>
      </div>

      {/* Photo drop zone */}
      <div style={{marginBottom:28}}>
        <label className="fl">Photos *</label>
        <div onClick={()=>fileRef.current?.click()} onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();addFiles(e.dataTransfer.files);}}
          style={{border:"2px dashed var(--bd)",borderRadius:10,padding:"32px 24px",textAlign:"center",cursor:"pointer",background:"rgba(255,255,255,.4)"}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:32,opacity:.25,marginBottom:8}}>◷</div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"#8A7E74"}}>DROP PHOTOS HERE OR CLICK TO BROWSE</div>
          {detecting && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"#B8860B",marginTop:8,letterSpacing:"0.1em"}}>AI DETECTING WATCH…</div>}
        </div>
        <input ref={fileRef} type="file" multiple accept="image/*" style={{display:"none"}} onChange={e=>addFiles(e.target.files)}/>
        {photos.length>0 && (
          <div style={{display:"flex",gap:10,marginTop:12,flexWrap:"wrap"}}>
            {photos.map((p,i)=>(
              <div key={i} style={{position:"relative"}}>
                <img src={p.preview} alt="" style={{width:80,height:80,objectFit:"cover",borderRadius:8,border:p.isPrimary?"2.5px solid #1A1612":"1.5px solid var(--bd)"}}/>
                {p.isPrimary && <div style={{position:"absolute",bottom:0,left:0,right:0,textAlign:"center",fontFamily:"'DM Mono',monospace",fontSize:7,color:"#fff",background:"rgba(0,0,0,.55)",borderRadius:"0 0 6px 6px",padding:"2px 0"}}>COVER</div>}
                <button onClick={()=>removePhoto(i)} style={{position:"absolute",top:-6,right:-6,width:18,height:18,borderRadius:"50%",background:"#C0392B",color:"#fff",border:"none",fontSize:12,cursor:"pointer",lineHeight:"18px",textAlign:"center"}}>×</button>
                {!p.isPrimary && <button onClick={()=>setPhotos(prev=>prev.map((x,j)=>({...x,isPrimary:j===i})))} style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,.4)",color:"#fff",border:"none",fontSize:7,cursor:"pointer",fontFamily:"'DM Mono',monospace",padding:"2px 0",borderRadius:"0 0 6px 6px"}}>COVER</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
        <div><label className="fl">Brand *</label><select value={form.brand} onChange={e=>sf("brand",e.target.value)}><option value="">Select</option><option>HMT</option><option>Allwyn</option><option>Ricoh</option></select></div>
        <div><label className="fl">Model *</label><input value={form.model} onChange={e=>sf("model",e.target.value)} placeholder="Pilot, Janata, Stride…"/></div>
        <div><label className="fl">Company</label><input value={form.company} onChange={e=>sf("company",e.target.value)} placeholder="HMT Limited, Allwyn Clock &amp; Watch Co.…"/></div>
        <div><label className="fl">Reference</label><input value={form.reference} onChange={e=>sf("reference",e.target.value)} placeholder="Ref no. if visible"/></div>
        <div><label className="fl">Year</label><input value={form.year} onChange={e=>sf("year",e.target.value)} placeholder="e.g. 1975"/></div>
        <div><label className="fl">Decade</label><select value={form.decade} onChange={e=>sf("decade",e.target.value)}><option value="">Unknown</option>{["1960s","1970s","1980s","1990s"].map(d=><option key={d}>{d}</option>)}</select></div>
        <div><label className="fl">Movement</label><select value={form.movement} onChange={e=>sf("movement",e.target.value)}><option value="">Unknown</option>{["Hand-wound","Automatic","Quartz"].map(m=><option key={m}>{m}</option>)}</select></div>
        <div><label className="fl">Condition</label><select value={form.condition} onChange={e=>sf("condition",e.target.value)}><option value="">Select</option>{["Mint","Excellent","Good","Fair"].map(c=><option key={c}>{c}</option>)}</select></div>
        <div><label className="fl">Price ₹ (optional)</label><input value={form.price} onChange={e=>sf("price",e.target.value)} placeholder="Leave blank if open"/></div>
        <div style={{gridColumn:"1/-1"}}><label className="fl">Description *</label><textarea value={form.description} onChange={e=>sf("description",e.target.value)} rows={4} placeholder="Dial, hands, case, movement condition. What makes this watch interesting?"/></div>
      </div>

      <button className="btn-ink" style={{fontSize:14,padding:"13px 28px",width:"100%",opacity:submitting?.6:1}} onClick={submit} disabled={submitting}>
        {submitting?"Uploading photos…":"Submit for review →"}
      </button>
    </div>
  );
}

// ─── Watch Labs ───────────────────────────────────────────────────────────────
// Community watch-design submissions + group pre-orders.
// Sellers/collectors submit a design; admin approves; users pre-order.
// At minOrders (default 10) pre-orders, design auto-flips to "funded".
